import React from "react";
import { useTranslation } from "react-i18next";

const Contact: React.FC = () => {
  // Contact details are stored in a structured way
  const { t } = useTranslation(); // Hook to use translations
  const marqueeItems = [
    t("contact.marqueePos"),
    t("contact.marqueeEbm"),
    t("contact.marqueeInventory"),
    t("contact.marqueeReports"),
    t("contact.marqueeExpenses"),
    t("contact.marqueeMultistore"),
    t("contact.marqueeReceipts"),
    t("contact.marqueeSupport"),
    t("contact.marqueeInsights"),
  ];

  const contactDetails = [
    {
      id: "location",
      title: t("contact.location"),
      content: (
        <>
          Kigali, Rwanda
          <br />
          Masoro, Special Economic Zone
        </>
      ),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="white"
          viewBox="0 0 24 24"
          width="40"
          height="40"
        >
          <path d="M12 2a7 7 0 0 1 7 7c0 3.75-3 7-7 11-4-4-7-7.25-7-11a7 7 0 0 1 7-7zM7 9a5 5 0 1 1 10 0c0 3.1-2.64 6-5 8.88C9.65 15.03 7 12.09 7 9z"></path>
          <circle cx="12" cy="9" r="2.5"></circle>
        </svg>
      ),
    },
    {
      id: "phone",
      title: t("contact.phone"),
      content: (
        <a href="tel:+250793090500" className="text-sm mb-4">
          +250 793 090 500
        </a>
      ),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="white"
          viewBox="0 0 24 24"
          width="40"
          height="40"
        >
          <path d="M6.62 10.79a15.53 15.53 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 .9-.27 11.36 11.36 0 0 0 3.6.6 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A16 16 0 0 1 3 5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .6 3.6 1 1 0 0 1-.26.94z"></path>
        </svg>
      ),
    },
    {
      id: "email",
      title: t("contact.email"),
      content: (
        <a href="mailto:info@hova.ai" className="text-sm mb-4 underline">
          info@hova.ai
        </a>
      ),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="white"
          viewBox="0 0 24 24"
          width="40"
          height="40"
        >
          <path d="M21 4H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1zM4 6h16l-8 6L4 6zm0 12V8l7.34 5.5a1 1 0 0 0 1.32 0L20 8v10H4z"></path>
        </svg>
      ),
    },
  ];

  return (
    <section className="bg-slate-950 text-white" id="contact">
      <div className="overflow-hidden border-y border-white/10 py-4">
        <div className="hs-marquee flex w-max text-sm font-bold uppercase tracking-[0.2em] text-slate-400">
          {[...Array(2)].map((_, groupIndex) => (
            <div key={groupIndex} className="flex shrink-0 items-center gap-6 pr-6">
              {marqueeItems.map((item) => (
                <React.Fragment key={`${groupIndex}-${item}`}>
                  <span className="whitespace-nowrap">{item}</span>
                  <span className="h-1.5 w-1.5 rounded-full bg-primary/80" />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-pink-200">
          {t("contact.eyebrow")}
        </p>
        <h1 className="mt-3 text-3xl font-bold sm:text-4xl">
          {t("contact.title", "Get in touch")}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-300">
          {t("contact.subtitle")}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {contactDetails.map(({ id, title, content, icon }) => (
            <div
              key={id}
              className="flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-xl shadow-black/20 transition hover:-translate-y-1 hover:bg-white/[0.07]"
            >
              <div className="mb-5 rounded-xl bg-primary p-4 shadow-lg shadow-primary/30">
                {icon}
              </div>
              <h2 className="mb-2 text-lg font-bold">{title}</h2>
              <p className="text-sm text-slate-300">{content}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="px-4 pb-16">
        <div className="text-center text-sm font-bold uppercase tracking-[0.2em] text-slate-400">
          {t("contact.follow", "Follow us")}
        </div>
        <div className="flex items-center justify-center gap-5 mt-6">
          <a
            href="https://www.linkedin.com/company/hova-ai/about/"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/20 p-4 transition hover:-translate-y-1 hover:bg-[#0077B5]"
          >
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 24V7.98h5V24H0zm7.75-13.02h4.3v2.1h.06c.6-1.1 2.07-2.26 4.26-2.26 4.56 0 5.4 3 5.4 6.9V24h-5v-5.48c0-1.3-.02-3.06-1.86-3.06-1.86 0-2.15 1.45-2.15 2.95V24h-5v-13.02h.01z" />
            </svg>
          </a>
          <a
            href="https://twitter.com/hova_ai"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/20 p-4 transition hover:-translate-y-1 hover:bg-[#1DA1F2]"
          >
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M23.954 4.569c-.885.39-1.83.654-2.825.774a4.92 4.92 0 0 0 2.165-2.723c-.951.555-2.005.959-3.127 1.184a4.917 4.917 0 0 0-8.384 4.482c-4.083-.205-7.699-2.158-10.126-5.134a4.822 4.822 0 0 0-.664 2.475c0 1.708.869 3.216 2.188 4.096a4.902 4.902 0 0 1-2.228-.616v.061a4.925 4.925 0 0 0 3.946 4.827 4.996 4.996 0 0 1-2.224.084 4.928 4.928 0 0 0 4.6 3.419A9.864 9.864 0 0 1 0 19.54a13.91 13.91 0 0 0 7.548 2.209c9.057 0 14.01-7.506 14.01-14.009 0-.21 0-.423-.015-.633A9.936 9.936 0 0 0 24 4.59z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/hova.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/20 p-4 transition hover:-translate-y-1 hover:bg-[#5b51d8]"
          >
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.206.056 2.032.246 2.666.512a5.387 5.387 0 0 1 1.949 1.262 5.388 5.388 0 0 1 1.263 1.949c.266.634.455 1.46.511 2.666.059 1.266.071 1.646.071 4.85s-.012 3.584-.07 4.85c-.056 1.206-.246 2.032-.512 2.666a5.387 5.387 0 0 1-1.262 1.949 5.388 5.388 0 0 1-1.949 1.263c-.634.266-1.46.455-2.666.511-1.266.059-1.646.071-4.85.071s-3.584-.012-4.85-.07c-1.206-.056-2.032-.246-2.666-.512a5.387 5.387 0 0 1-1.949-1.262 5.388 5.388 0 0 1-1.263-1.949c-.266-.634-.455-1.46-.511-2.666-.059-1.266-.071-1.646-.071-4.85s.012-3.584.07-4.85c.056-1.206.246-2.032.512-2.666A5.387 5.387 0 0 1 2.12 3.443 5.388 5.388 0 0 1 4.07 2.18c.634-.266 1.46-.455 2.666-.511C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.695 0 8.262.013 7.005.071c-1.259.059-2.12.247-2.847.524a7.48 7.48 0 0 0-2.732 1.77 7.478 7.478 0 0 0-1.77 2.732c-.276.728-.465 1.588-.524 2.847C0 8.262 0 8.695 0 12c0 3.305.013 3.738.071 4.995.059 1.259.247 2.12.524 2.847a7.48 7.48 0 0 0 1.77 2.732 7.478 7.478 0 0 0 2.732 1.77c.728.276 1.588.465 2.847.524C8.262 24 8.695 24 12 24s3.738-.013 4.995-.071c1.259-.059 2.12-.247 2.847-.524a7.48 7.48 0 0 0 2.732-1.77 7.478 7.478 0 0 0 1.77-2.732c.276-.728.465-1.588.524-2.847.059-1.257.071-1.69.071-4.995s-.013-3.738-.071-4.995c-.059-1.259-.247-2.12-.524-2.847a7.48 7.48 0 0 0-1.77-2.732 7.478 7.478 0 0 0-2.732-1.77c-.728-.276-1.588-.465-2.847-.524C15.738.013 15.305 0 12 0zm0 5.838a6.162 6.162 0 1 0 6.162 6.162A6.162 6.162 0 0 0 12 5.838zm0 10.162a3.999 3.999 0 1 1 3.999-3.999A3.999 3.999 0 0 1 12 16zm6.406-11.845a1.44 1.44 0 1 1-1.44-1.44 1.44 1.44 0 0 1 1.44 1.44z" />
            </svg>
          </a>
          <a
            href="https://www.tiktok.com/@hova.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/20 p-4 transition hover:-translate-y-1 hover:bg-black"
          >
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-2-2.75v-3.5a6.33 6.33 0 1 0 5.45 6.25V8.82a8.16 8.16 0 0 0 4.77 1.52V6.69z" />
            </svg>
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 bg-white/[0.03] py-4 text-center text-[12px] font-bold uppercase tracking-widest text-slate-400">
        HovaStore, {t("contact.footer", "Get in touch")} Hova AI
      </div>
    </section>
  );
};

export default Contact;
